import * as React from 'react';
import { createChainedFunction } from '../../utils';
import { useRadioGroupContext } from '../Radio/RadioGroup/RadioGroup.context';
import { SwitchChipBase, SwitchChipBaseProps } from '../SwitchChipBase/SwitchChipBase';

export type RadioChipProps = SwitchChipBaseProps;

/**
 * Radio chip is an extension to radio component where it displays the Radio as a [Chip](?path=/docs/honeycomb-ui-core-display-chip--documentation) implementation
 * allowing and extending the same format of the radio but displayed as chip. It can support for icons to be taken in and displayed in the chip as well and can be
 * extended to be shown in disabled state. It can take in any form of children to it and display it as the content of RadioChip.
 */
export const RadioChip: React.ForwardRefExoticComponent<RadioChipProps & React.RefAttributes<HTMLElement>> =
    React.forwardRef(function RadioChip(
        props: RadioChipProps,
        ref: React.ForwardedRef<HTMLElement>
    ): React.JSX.Element {
        const { checked: checkedProp, name: nameProp, onChange: onChangeProp, value: valueProp, ...other } = props;

        // The component may be a member of a radio group
        const radioGroup = useRadioGroupContext();

        let checked = checkedProp;
        let onChange = onChangeProp;
        let name = nameProp;

        if (radioGroup) {
            if (onChangeProp && radioGroup.onChange) {
                onChange = createChainedFunction(onChangeProp, radioGroup.onChange);
            } else {
                onChange = radioGroup.onChange;
            }
            if (typeof checked === 'undefined') {
                checked = radioGroup.value === valueProp;
            }
            if (typeof name === 'undefined') {
                name = radioGroup.name;
            }
        }

        return (
            <SwitchChipBase
                ref={ref}
                type="radio"
                onChange={onChange}
                checked={checked}
                name={name}
                value={valueProp}
                {...other}
            />
        );
    });
